// src/LoginPage.js
import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Spinner } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './LoginPage.css';
import API_BASE_URL from './config/api';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userParam = params.get('user');
    if (token && userParam) {
      try {
        const userObj = JSON.parse(decodeURIComponent(userParam));
        localStorage.setItem('managerToken', token);
        localStorage.setItem('managerUser', JSON.stringify(userObj));
        // Flag that we just logged in so the in-suite Dashboard can decide to
        // smart-redirect to a tab with data (e.g. dryer/project) when this
        // building has no in-suite cycles to show.
        sessionStorage.setItem('justLoggedIn', '1');
        window.history.replaceState({}, '', '/login');
        window.location.href = '/';
      } catch (_) {}
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/units/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        const { msg } = await response.json();
        setError(msg || 'Login failed');
        return;
      }

      const data = await response.json();

      let userObj = data.user;
      if (userObj.buildings && userObj.buildings.length > 0) {
        userObj.buildingId = userObj.buildings[0];
      }

      localStorage.setItem('managerToken', data.token);
      localStorage.setItem('managerUser', JSON.stringify(userObj));

      if (typeof window.clarity === 'function') {
        const displayName = [userObj.first_name, userObj.last_name].filter(Boolean).join(' ') || username;
        window.clarity('identify', username, null, null, displayName);
        window.clarity('event', 'manager_login');
        if (userObj.role) window.clarity('set', 'role', userObj.role);
        if (userObj.management_company) window.clarity('set', 'management_company', userObj.management_company);
      }

      // Check if there's a saved redirect URL (from ProtectedRoute)
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');

      // Flag that we just logged in so the in-suite Dashboard can decide to
      // smart-redirect to a tab with data (e.g. dryer/project) when this
      // building has no in-suite cycles to show. Only set the flag when we
      // know we'll land on the in-suite Dashboard ('/'), otherwise it would
      // sit in sessionStorage and fire a stale redirect later.
      const willLandOnDashboard = !redirectUrl || redirectUrl === '/' || redirectUrl === '';
      if (willLandOnDashboard) {
        sessionStorage.setItem('justLoggedIn', '1');
      }
      
      if (redirectUrl) {
        // Clear the saved redirect URL
        sessionStorage.removeItem('redirectAfterLogin');
        console.log('Redirecting to saved destination:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        // Default redirect to home
        window.location.href = '/';
      }
    } catch (err) {
      setError('Error logging in. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ background: '#2c487d', minHeight: '100vh', position: 'relative' }}>
      
      {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PROFILE_PREVIEW === 'true' && <div style={{position:'relative',zIndex:3,padding:'18px 24px',background:'#edf4ff',color:'#18345a',lineHeight:1.6}}>
        <strong>Local Profile test — changes stay in a separate test database.</strong><br/>
        Manager: <strong>manager.preview</strong> · Hudson staff: <strong>hudson.preview</strong><br/>
        Password for both: <strong>HudsonLocal2026!</strong>
      </div>}
      {/* Background Image Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/downtown.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 1,
        }}
      />
      
      {/* Top Navbar with logo & signup button */}
      <div
        style={{
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <img
          src="https://hudsonhvac.ca/images/logo-w.png"
          alt="Hudson White Logo"
          style={{ height: '40px' }}
        />
        <Button
          variant="light"
          style={{ color: '#000', fontWeight: '600' }}
          onClick={() => window.location.href = 'https://hudsonhvac.ca/signup'}
        >
          Sign Up
        </Button>
      </div>

      <Container
        style={{
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          position: 'relative',
          zIndex: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="login-card">
          <div className="mb-3">
            <img
              src="https://hudsonhvac.ca/images/logo.png"
              alt="Hudson Logo"
              style={{ maxWidth: '100px', marginBottom: '1rem' }}
            />
          </div>

          <h2 style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '1rem', color: '#000' }}>
            Manager Login
          </h2>
          <p
            style={{
              fontSize: '1.1rem',
              marginBottom: '2rem',
              color: '#6c757d',
              fontWeight: 200,
            }}
          >
            Sign in to access your Hudson Dashboard
          </p>

          <Form onSubmit={handleLogin}>
            {/* Work Email */}
            <Form.Group controlId="username" className="mobile-form mb-4" style={{ textAlign: 'left' }}>
              <Form.Label style={{ fontWeight: 400, marginBottom: '0.5rem', color: '#000' }}>
                Work Email <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="manager@example.com"
                required
                style={{
                  borderRadius: '8px',
                  padding: '0.8rem 1rem',
                  fontSize: '1rem',
                }}
              />
            </Form.Group>

            {/* Password with Eye Icon */}
            <Form.Group
              controlId="password"
              className="mb-4"
              style={{ textAlign: 'left', position: 'relative' }}
            >
              <Form.Label
                style={{ fontWeight: 400, marginBottom: '0.5rem', color: '#000' }}
              >
                Password <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{
                  borderRadius: '8px',
                  padding: '0.8rem 2.5rem 0.8rem 1rem', // extra right padding for icon
                  fontSize: '1rem',
                  lineHeight: '1.5', // helps center the text
                }}
              />
              {/* Eye Icon */}
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  top: '55px',
                  right: '0.75rem',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '1.25rem', // matches the input's line-height
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </Form.Group>

            {error && (
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="btn-submit"
              style={{
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1.1rem',
                width: '100%',
                backgroundColor: '#678fd6',
                border: 'none',
                fontWeight: 600,
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: '0.5rem' }}
                  />
                  Logging in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            <center>
              <p style={{ width: '100%', marginTop: '1.5rem', color: '#888', fontSize: '0.9rem' }}>
                Not using Hudson?{' '}
                <a
                  href="https://hudsonhvac.ca/signup"
                  style={{ textDecoration: 'underline', color: '#2c59a2', fontWeight: '600' }}
                >
                  Register
                </a>
              </p>
            </center>
          </Form>
        </div>
      </Container>
    </div>
  );
}

export default LoginPage;
